import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { scheduleApi } from "../api/schedule";

export function useSchedule(start?: string, end?: string, includeInactive?: boolean) {
  return useQuery({
    queryKey: ["schedule", start, end, includeInactive ?? false],
    queryFn: () => scheduleApi.getSchedule(start, end, includeInactive),
    staleTime: 0,
  });
}

export function useScheduleEntries(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["schedule-entries", params],
    queryFn: () => scheduleApi.listEntries(params),
    staleTime: 0,
  });
}

export function useCreateScheduleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { entry_type: string; start_date: string; end_date: string; note?: string }) =>
      scheduleApi.createEntry(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["schedule-entries"] });
    },
  });
}

export function useDeleteScheduleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduleApi.deleteEntry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedule"] }),
  });
}

export function useApproveScheduleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      scheduleApi.approveEntry(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["schedule-entries"] });
    },
  });
}

export function useRejectScheduleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      scheduleApi.rejectEntry(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["schedule-entries"] });
    },
  });
}

export function useSetRemoteHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { start_hour?: string | null; hours_ranges?: Array<{ start: string; end: string }> | null } }) =>
      scheduleApi.setEntryHours(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedule"] }),
  });
}

export function useCompanyHolidays() {
  return useQuery({
    queryKey: ["company-holidays"],
    queryFn: () => scheduleApi.listCompanyHolidays(),
  });
}

export function useCreateCompanyHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { start_date: string; end_date: string; description?: string }) =>
      scheduleApi.createCompanyHoliday(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["company-holidays"] });
    },
  });
}

export function useDeleteCompanyHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduleApi.deleteCompanyHoliday(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["company-holidays"] });
    },
  });
}
